import { InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import TagId from '~/constants/common/TagId';
import Store from '~/constants/store';
import type { VehicleDetails, YmmtWhyBuy } from '~/contracts/vdp';
import type { DeepPartial } from '~/types';
import { transformExteriorImages } from '~/utilities/vdp/imagery';

export type ContextType = {
  fetchWhyBuyFlag: number;
  similarVehicleCount: LoadableState<number>;
  triggerFetchWhyBuy: () => void;
  vehicleDetails: Partial<VehicleDetails>;
  ymmtWhyBuy: LoadableState<YmmtWhyBuy>;
};

const defaultContext: ContextType = {
  fetchWhyBuyFlag: 0,
  similarVehicleCount: defaultLoadableState(0),
  triggerFetchWhyBuy: () => {},
  vehicleDetails: {},
  ymmtWhyBuy: defaultLoadableState<YmmtWhyBuy>({
    bulletPoints: [],
    summary: null
  })
};

export const VehicleContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  vehicleDetails: Partial<VehicleDetails>;
}>;

export const VehicleContextProvider = ({
  children,
  vehicleDetails
}: OwnProps) => {
  const similarVehicleCount = useLoadableState<number>(
    defaultContext.similarVehicleCount.data
  );
  const ymmtWhyBuy = useLoadableState<YmmtWhyBuy>(
    defaultContext.ymmtWhyBuy.data
  );
  const [fetchWhyBuyFlag, setFetchWhyBuyFlag] = useState(
    defaultContext.fetchWhyBuyFlag
  );

  const triggerFetchWhyBuy = useCallback(
    () => setFetchWhyBuyFlag(current => current + 1),
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      fetchWhyBuyFlag,
      similarVehicleCount,
      triggerFetchWhyBuy,
      vehicleDetails,
      ymmtWhyBuy
    }),
    [
      fetchWhyBuyFlag,
      similarVehicleCount,
      triggerFetchWhyBuy,
      vehicleDetails,
      ymmtWhyBuy
    ]
  );

  return (
    <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>
  );
};

VehicleContextProvider.displayName = 'VehicleContextProvider';

export default VehicleContext;

export const generateVehicleContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedVehicleContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <VehicleContext.Provider value={generateVehicleContext(mockValue)}>
      {children}
    </VehicleContext.Provider>
  );
}

export function useGetVehicleDetails() {
  return useContext(VehicleContext).vehicleDetails;
}

export function useInspectionReport() {
  return useGetVehicleDetails().inspectionReport;
}

export function useOpenRecalls() {
  return useGetVehicleDetails().recalls ?? [];
}

export function useGetYmmtWhyBuy() {
  return useContext(VehicleContext).ymmtWhyBuy.data;
}

export function useGetStores() {
  return useGetVehicleDetails().stores ?? [];
}

export function useGetVehicleId() {
  return useGetVehicleDetails().vehicleId ?? 0;
}

export function useGetVexVdpImageData() {
  return useGetVehicleDetails().vexVdpImageData ?? {};
}

export function useGetVehicleStockNumber() {
  return useGetVehicleDetails().stockNumber ?? 0;
}

export function useGetVehicleIdAndStockNumber() {
  return {
    stockNumber: useGetVehicleStockNumber(),
    vehicleId: useGetVehicleId()
  };
}

export function useGetVehicleInfoForSpinner() {
  const { installedPackages = [], make } = useGetVehicleDetails();

  return { installedPackages, make };
}

/**
 * Determines if a vehicle is a WIP or not. Very rarely do you need this
 * information. Only in cases of imagery should this be used. In most cases you
 * should use `useGetIsReservableVehicle` to determine if a vehicle is
 * reservable or not.
 */
export function useGetIsWIPVehicle() {
  return useGetVehicleDetails().inventoryType === InventoryTypeKey.WIP;
}

export function useIsNewVehicle() {
  return useGetVehicleDetails().offering === VehicleOffering.New;
}

export function useGetActivationModalVehicleData() {
  const stockNumber = useGetVehicleStockNumber();
  const {
    make = '',
    mileage = 0,
    model = '',
    price = 0,
    trim = '',
    vehicleId = 0,
    stockImageData,
    year = 0
  } = useGetVehicleDetails();
  const isWIPVehicle = useGetIsWIPVehicle();

  return {
    imageUrl: isWIPVehicle
      ? stockImageData?.jellyBeanDesktopUrl ?? ''
      : `/${stockNumber}/post-large/${stockNumber}-edc-02-search.jpg`,
    isOnDemand: isWIPVehicle,
    make,
    mileage,
    model,
    price: { total: price },
    stockNumber,
    trim,
    vehicleId,
    vehicleLockType: 0,
    year
  };
}

export function useGetIsRecentlyAdded() {
  return (
    useGetVehicleDetails().highlights?.some(
      ({ tagId }) => tagId === TagId.RecentlyAdded
    ) || false
  );
}

export function useGetIsLightAccidentVehicle() {
  return useGetStores().includes(Store.LightAccident);
}

export function useGetSpinnerData() {
  return useGetVehicleDetails().vexVdpImageData || {};
}

export function useGetHasImages() {
  const { features = [], imperfections = [], spins = [] } = useGetSpinnerData();

  return Boolean(
    features.length ||
      imperfections.length ||
      transformExteriorImages(spins).length
  );
}

export function useGetIsDealerMarketplace() {
  const { inventoryType, stores = [] } = useGetVehicleDetails();

  return (
    inventoryType === InventoryTypeKey.DealerMarketplace ||
    stores.includes(Store.DMP2Core)
  );
}

export function useGetIs3PR() {
  return useGetStores().includes(Store.Manheim);
}

export function useGetIsPartnerRetailVehicle() {
  return useGetStores().includes(Store.PartnerRetail);
}

export function useGetIsPartnerVehicle() {
  const isMarketplace = useGetIsDealerMarketplace();
  const is3PR = useGetIs3PR();

  return isMarketplace || is3PR;
}

export function useGetLastModified() {
  return (
    useGetVehicleDetails().vexVdpImageData?.captureInfo?.lastModified ?? ''
  );
}
